import { defineStore } from "pinia";

import { computed, ref } from "vue";

import { useChatStore } from "@/stores/chat/chat.store";
import {
  type ChatBloDto,
  RequestStatus,
  type PaginationConfig,
  type AdminChatFilterDto,
  ADMIN_REQUEST_NAME_BY_STATUS,
  type ChatDto,
  type ChatIdsFilterDto,
} from "@/api/api.dto";

export const useSupportAdminStore = defineStore("useSupportAdminStore", () => {
  const chatStore = useChatStore();

  // Keep serviceTypes and services as mocked data (no API equivalent)
  const serviceTypes = ref([
    {
      id: "ServiceTypeId1",
      name: "Служба поддержки (Все)",
    },
  ]);

  const services = ref([
    {
      id: "ServiceId1",
      name: "Support #1",
      type: "ServiceTypeId1",
      count: 0,
    },
  ]);

  const showAllRequests = ref<boolean>(false);
  const searchFilter = ref<string | null>(null);

  const requestsFilter = computed<
    Partial<Record<RequestStatus, AdminChatFilterDto>>
  >(() => ({
    [RequestStatus.NEW]: {
      freeChats: true,
      skip:
        (pagination.value[RequestStatus.NEW]!.current - 1) *
        pagination.value[RequestStatus.NEW]!.pageSize,
      take: pagination.value[RequestStatus.NEW]!.pageSize,
      forAll: showAllRequests.value,
      isClosed: false,
      isSolved: false,
      searchText: searchFilter.value,
      isOrderByDesc: true,
    },
    [RequestStatus.IN_WORK]: {
      freeChats: false,
      skip:
        (pagination.value[RequestStatus.IN_WORK]!.current - 1) *
        pagination.value[RequestStatus.IN_WORK]!.pageSize,
      take: pagination.value[RequestStatus.IN_WORK]!.pageSize,
      forAll: showAllRequests.value,
      isClosed: false,
      isSolved: false,
      searchText: searchFilter.value,
      isOrderByDesc: true,
    },
    [RequestStatus.ANSWERED]: {
      freeChats: false,
      skip:
        (pagination.value[RequestStatus.ANSWERED]!.current - 1) *
        pagination.value[RequestStatus.ANSWERED]!.pageSize,
      take: pagination.value[RequestStatus.ANSWERED]!.pageSize,
      forAll: showAllRequests.value,
      isClosed: false,
      isSolved: true,
      searchText: searchFilter.value,
      isOrderByDesc: true,
    },
    [RequestStatus.CLOSED]: {
      skip:
        (pagination.value[RequestStatus.CLOSED]!.current - 1) *
        pagination.value[RequestStatus.CLOSED]!.pageSize,
      take: pagination.value[RequestStatus.CLOSED]!.pageSize,
      forAll: showAllRequests.value,
      isClosed: true,
      searchText: searchFilter.value,
      isOrderByDesc: true,
    },
  }));

  const requestsIdsFilter = computed<
    Partial<Record<RequestStatus, ChatIdsFilterDto>>
  >(() => ({
    [RequestStatus.NEW]: {
      freeChats: true,
      forAll: showAllRequests.value,
      isClosed: false,
      isSolved: false,
      isOrderByDesc: true,
    },
    [RequestStatus.IN_WORK]: {
      freeChats: false,
      forAll: showAllRequests.value,
      isClosed: false,
      isSolved: false,
      isOrderByDesc: true,
    },
    [RequestStatus.ANSWERED]: {
      freeChats: false,
      forAll: showAllRequests.value,
      isClosed: false,
      isSolved: true,
      isOrderByDesc: true,
    },
    [RequestStatus.CLOSED]: {
      forAll: showAllRequests.value,
      isClosed: true,
      isOrderByDesc: true,
    },
  }));

  const requests = ref<Record<RequestStatus, ChatBloDto[]>>({
    [RequestStatus.NEW]: [],
    [RequestStatus.IN_WORK]: [],
    [RequestStatus.ANSWERED]: [],
    [RequestStatus.CLOSED]: [],
  });

  const requestsIds = ref<Record<RequestStatus, number[]>>({
    [RequestStatus.NEW]: [],
    [RequestStatus.IN_WORK]: [],
    [RequestStatus.ANSWERED]: [],
    [RequestStatus.CLOSED]: [],
  });

  const getAdminChatsByStatus = (status: RequestStatus) => {
    return async (_page: number, _pageSize: number) => {
      const requestsInfo: ChatDto | null | undefined =
        await chatStore.fetchAdminChats(
          requestsFilter.value[status] as AdminChatFilterDto,
        );
      requests.value[status] = requestsInfo?.chats || [];
      pagination.value[status]!.total = requestsInfo?.count || 0;
    };
  };

  const getUserChatsIdsByStatus = async (status: RequestStatus) => {
    const ids: number[] = await chatStore.getExistenRequestsIds(
      requestsIdsFilter.value[status] as ChatIdsFilterDto,
    );
    requestsIds.value[status] = ids || [];
  };

  const pagination = ref<Partial<Record<RequestStatus, PaginationConfig>>>({
    [RequestStatus.NEW]: {
      current: 1,
      pageSize: 5,
      defaultPageSize: 5,
      total: 0,
      showQuickJumper: false,
      showSizeChanger: false,
      showLessItems: true,
      pageSizeOptions: [5],
      onChangePagination: getAdminChatsByStatus(RequestStatus.NEW),
    },
    [RequestStatus.IN_WORK]: {
      current: 1,
      pageSize: 5,
      defaultPageSize: 5,
      total: 0,
      showQuickJumper: false,
      showSizeChanger: false,
      showLessItems: true,
      pageSizeOptions: [5],
      onChangePagination: getAdminChatsByStatus(RequestStatus.IN_WORK),
    },
    [RequestStatus.ANSWERED]: {
      current: 1,
      pageSize: 5,
      defaultPageSize: 5,
      total: 0,
      showQuickJumper: false,
      showSizeChanger: false,
      showLessItems: true,
      pageSizeOptions: [5],
      onChangePagination: getAdminChatsByStatus(RequestStatus.ANSWERED),
    },
    [RequestStatus.CLOSED]: {
      current: 1,
      pageSize: 5,
      defaultPageSize: 5,
      total: 0,
      showQuickJumper: false,
      showSizeChanger: false,
      showLessItems: true,
      pageSizeOptions: [5],
      onChangePagination: getAdminChatsByStatus(RequestStatus.CLOSED),
    },
  });

  const groupedRequestsByStatus = computed(() => {
    return Object.entries(requests.value).map(([status, requests], index) => ({
      statusName: ADMIN_REQUEST_NAME_BY_STATUS[
        status as unknown as RequestStatus
      ] as string,
      status: +status as RequestStatus,
      id: index.toFixed(),
      requests: requests.map((chat) => ({
        status: +status as RequestStatus,
        theme: chat.title || "Без темы",
        number: parseInt(chat.applicationNumber || "0", 10),
        id: chat.id,
        userId: chat.userId,
        userName: chat.userName,
        partnerId: chat.partnerId,
        createdDate: chat.createdDate,
      })),
    }));
  });

  const fetchAllAdminChats = async () => {
    await Promise.all([
      getAdminChatsByStatus(RequestStatus.NEW)(
        pagination.value[RequestStatus.NEW]!.current,
        pagination.value[RequestStatus.NEW]!.pageSize,
      ),
      getAdminChatsByStatus(RequestStatus.IN_WORK)(
        pagination.value[RequestStatus.IN_WORK]!.current,
        pagination.value[RequestStatus.IN_WORK]!.pageSize,
      ),
      getAdminChatsByStatus(RequestStatus.ANSWERED)(
        pagination.value[RequestStatus.ANSWERED]!.current,
        pagination.value[RequestStatus.ANSWERED]!.pageSize,
      ),
      getAdminChatsByStatus(RequestStatus.CLOSED)(
        pagination.value[RequestStatus.CLOSED]!.current,
        pagination.value[RequestStatus.CLOSED]!.pageSize,
      ),
    ]);
  };

  const fetchAllUserChatsIds = async () => {
    await Promise.all([
      getUserChatsIdsByStatus(RequestStatus.NEW),
      getUserChatsIdsByStatus(RequestStatus.IN_WORK),
      getUserChatsIdsByStatus(RequestStatus.ANSWERED),
      getUserChatsIdsByStatus(RequestStatus.CLOSED),
    ]);
  };

  // Initialize data with filters for admin
  const initialize = async () => {
    await fetchAllAdminChats();
    await chatStore.connectWebSocket();
  };

  // Admin-specific actions
  const assignAdminToChat = async (chatId: string) => {
    const numericChatId = parseInt(chatId, 10);
    await chatStore.assignAdminToChat(numericChatId);
  };

  return {
    serviceTypes,
    services,
    requests,
    requestsIds,
    initialize,
    assignAdminToChat,
    pagination,
    groupedRequestsByStatus,
    showAllRequests,
    searchFilter,
    fetchAllAdminChats,
    fetchAllUserChatsIds,
  };
});
